import * as React from "react";
import { useSelector, useDispatch, useStore } from "react-redux";
import { Dispatch, Store } from "redux";

export const useReduxState = (defaultKeys: string[]) => {
  const [keys, setKeys] = React.useState(defaultKeys);

  const getKeys = React.useMemo(() => {
    return (state) => {
      return keys.reduce((acc, key, idx) => {
        if (!acc) {
          return undefined;
        }
        else if (!acc[key]) {
          const fullKey = keys.reduce((a, k, i) => a + '.' + k, '');
          if (process.env.NODE_ENV === 'development') {
            console.log(`useRedux: No value found in redux state for key '${fullKey}'`);
          }
          return undefined;
        } else {
          return acc[key];
        }
      }, state);
    };
  }, [keys]);

  const value = useSelector(getKeys);

  return {
    value,
    setKeys,
  };
};

type ActionFn<P> = (params?: P) => (dispatch: Dispatch, store?: Store) => Promise<void>;

export const useReduxAction = <P>(action: ActionFn<P>) => {
  const dispatch = useDispatch();  
  const store = useStore();

  const dispatchCallback = (params?: P): Promise<void> => {
    return action(params)(dispatch, store);
  };

  return dispatchCallback;
};

export const useReduxActions = <P extends { [K: string] : ActionFn<any>}>(actions: P) => {
  const dispatch = useDispatch();  
  const store = useStore();

  const dispatchCallbacks = Object.keys(actions).reduce((acc, key) => {
    const cb = (...params): Promise<void> => {
      return actions[key](...params)(dispatch, store);
    };
    acc[key] = cb;
    return acc;
  }, {});

  return dispatchCallbacks as {
    [K in keyof P]: (...params: Parameters<P[K]>) => Promise<void>;
  };
};
